import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { FileText, Plus, Trash2, Save } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Admin settings card for the business-wide default notes format.
 *
 *   - Free-form: a single "Notes" textarea (current behaviour, default)
 *   - SOAP:      Subjective / Objective / Assessment / Plan
 *   - Custom:    admin-defined labels (1..12)
 *
 * Per-service overrides are deferred to a follow-up. For most businesses a
 * single default covers all services.
 *
 * Storage uses two business_info keys via the existing PUT endpoint:
 *   - notes_default_format
 *   - notes_default_custom_labels (JSON array, ignored unless format=custom)
 */
export default function NotesFormatEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [format, setFormat] = useState('free_form');
  const [customLabels, setCustomLabels] = useState([]);
  const [newLabel, setNewLabel] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem('token');
        const res = await axios.get(`${API_URL}/notes-format/defaults`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFormat(res.data.format || 'free_form');
        setCustomLabels(Array.isArray(res.data.customLabels) ? res.data.customLabels : []);
      } catch (e) {
        // Falls back silently to defaults; editor remains usable.
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addLabel = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    if (customLabels.length >= 12) {
      toast.error('Maximum 12 custom labels');
      return;
    }
    if (customLabels.some((l) => l.toLowerCase() === trimmed.toLowerCase())) {
      toast.error('Label already exists');
      return;
    }
    setCustomLabels([...customLabels, trimmed]);
    setNewLabel('');
  };

  const removeLabel = (idx) => {
    setCustomLabels(customLabels.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (format === 'custom' && customLabels.length === 0) {
      toast.error('Add at least one label or pick a different format');
      return;
    }
    setSaving(true);
    try {
      const token = sessionStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      // The business-info PUT endpoint accepts arbitrary keys; just persist
      // both fields. No batch endpoint exists, so do them sequentially.
      await axios.put(
        `${API_URL}/business-info/notes_default_format`,
        { value: format },
        { headers }
      );
      await axios.put(
        `${API_URL}/business-info/notes_default_custom_labels`,
        { value: JSON.stringify(customLabels) },
        { headers }
      );
      toast.success('Notes format saved');
    } catch (e) {
      toast.error('Failed to save notes format');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="animate-pulse text-gray-400 text-sm">Loading notes format…</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="flex items-center space-x-2 mb-3">
        <FileText className="text-primary-600" size={22} />
        <h3 className="text-lg font-bold text-gray-900">Notes format</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">
        Choose how staff record notes on appointments. Applies to every
        appointment until you change it. Existing notes are never deleted —
        if you switch formats, previous notes stay readable in the editor.
      </p>

      <div className="space-y-3 mb-4">
        <FormatRadio
          value="free_form"
          current={format}
          onChange={setFormat}
          title="Free-form (default)"
          description="A single text area. Best for businesses that don't need structured records — pet groomers, salons, tattoo studios, fitness coaches, etc."
        />
        <FormatRadio
          value="soap"
          current={format}
          onChange={setFormat}
          title="SOAP (Subjective / Objective / Assessment / Plan)"
          description="Four labelled fields. Standard format in clinical, wellness, and medical-adjacent practices (massage, chiro, physio, naturopath, etc.)."
        />
        <FormatRadio
          value="custom"
          current={format}
          onChange={setFormat}
          title="Custom labels"
          description="Define your own field labels — for example, History / Treatment / Follow-up; or Intake / Procedure / Outcome."
        />
      </div>

      {format === 'custom' && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
          <h4 className="text-sm font-semibold text-gray-800 mb-2">Custom labels</h4>
          {customLabels.length === 0 && (
            <p className="text-xs text-gray-500 mb-2">No labels yet. Add at least one.</p>
          )}
          <div className="space-y-2 mb-3">
            {customLabels.map((label, idx) => (
              <div key={idx} className="flex items-center justify-between bg-white border border-gray-200 rounded px-3 py-2">
                <span className="text-sm text-gray-800">{label}</span>
                <button
                  type="button"
                  onClick={() => removeLabel(idx)}
                  className="text-red-600 hover:text-red-800"
                  aria-label={`Remove ${label}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex space-x-2">
            <input
              type="text"
              className="input flex-1"
              placeholder="Label name (e.g. History)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLabel(); } }}
              maxLength={50}
            />
            <button
              type="button"
              onClick={addLabel}
              className="btn btn-secondary flex items-center space-x-1"
            >
              <Plus size={14} />
              <span>Add</span>
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Up to 12 labels. Order matters — they render top-to-bottom in
            the notes editor.
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary flex items-center space-x-2"
        >
          <Save size={16} />
          <span>{saving ? 'Saving…' : 'Save notes format'}</span>
        </button>
      </div>
    </div>
  );
}

function FormatRadio({ value, current, onChange, title, description }) {
  const checked = current === value;
  return (
    <label className={`flex items-start space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
      checked ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
    }`}>
      <input
        type="radio"
        name="notes_default_format"
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="mt-1"
      />
      <div>
        <div className="font-semibold text-gray-900 text-sm">{title}</div>
        <div className="text-xs text-gray-600 mt-0.5">{description}</div>
      </div>
    </label>
  );
}
